import type Phaser from 'phaser';
import type { GameEvent } from '../../core/events';
import { eventToSfxKey, voiceLinePrefix } from './eventSound';
import { AudioSettingsService } from '../../services/audioSettingsService';

/** Minimum gap between two plays of the same key (ms) — prevents audio spam. */
const THROTTLE_MS = 60;
/** High-frequency keys played at reduced volume so swarms/rapid fire don't blare. */
const QUIET_KEYS = new Set([
  'sfx-die-fry',
  'sfx-fire-patty',
  'sfx-fire-pickle',
  'sfx-fire-bacon',
  'sfx-fire-sauce',
]);
const QUIET_SCALE = 0.6;

/**
 * Plays Hambi SFX through Phaser's WebAudio manager. No-ops when an audio key
 * is not loaded (pre-generation / headless) so the game runs without sound.
 * Applies the persisted mute + master volume to the game-global sound manager.
 */
export class AudioService {
  private readonly lastPlayed = new Map<string, number>();
  private introSound?: Phaser.Sound.BaseSound;

  constructor(
    private readonly game: Phaser.Game,
    private readonly settings: AudioSettingsService,
  ) {
    this.applySettings();
  }

  /** Push the persisted mute + volume to the global sound manager. */
  applySettings(): void {
    const s = this.settings.get();
    this.game.sound.mute = s.muted;
    this.game.sound.volume = s.volume;
  }

  playEvent(e: GameEvent): void {
    // For events with a custom voice-line pool (wave-start cries, episode-loss
    // taunts), the randomly-chosen voice line REPLACES the base SFX — playing
    // both layered them into a doubled sound. Fall back to the base SFX only
    // when no voice clips are bundled (pre-build / headless).
    const prefix = voiceLinePrefix(e);
    if (prefix && this.playRandomVariant(prefix)) return;
    const key = eventToSfxKey(e);
    if (key) this.play(key);
  }

  /**
   * Play one randomly-chosen loaded clip whose key starts with `prefix`. The
   * pool is read live from the audio cache so newly-added clips are picked up
   * automatically. Returns true if a clip was played, false when the pool is
   * empty (so the caller can fall back to the base SFX).
   */
  private playRandomVariant(prefix: string): boolean {
    const pool = this.game.cache.audio.getKeys().filter((k) => k.startsWith(prefix));
    if (pool.length === 0) return false;
    const key = pool[Math.floor(Math.random() * pool.length)];
    this.play(key);
    return true;
  }

  play(key: string): void {
    if (!this.game.cache.audio.exists(key)) return; // not generated / headless
    const now = this.game.loop.time;
    const last = this.lastPlayed.get(key) ?? -Infinity;
    if (now - last < THROTTLE_MS) return;
    this.lastPlayed.set(key, now);
    this.game.sound.play(key, { volume: QUIET_KEYS.has(key) ? QUIET_SCALE : 1 });
  }

  /**
   * Play a menu intro sting. No-ops if one is already playing — so mashing or
   * re-clicking Play never overlaps or restarts it — or if the key isn't loaded.
   * Played from the Play-button click, which is also the gesture that unlocks
   * browser audio, so the sting reliably starts.
   */
  playIntro(key: string): void {
    if (this.introSound?.isPlaying) return;
    if (!this.game.cache.audio.exists(key)) return; // not generated / headless
    const sound = this.game.sound.add(key);
    this.introSound = sound;
    sound.play();
  }
}

/** One shared AudioService per game, stored on the registry. */
export function getAudioService(scene: Phaser.Scene): AudioService {
  const reg = scene.game.registry;
  let svc = reg.get('audioService') as AudioService | undefined;
  if (!svc) {
    svc = new AudioService(scene.game, new AudioSettingsService(window.localStorage));
    reg.set('audioService', svc);
  }
  return svc;
}
